import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { Project, Customer } from '../types';

const statusLabels: Record<string, string> = {
  INQUIRY: 'Anfrage', OFFER_SENT: 'Angebot gesendet', ORDER: 'Auftrag',
  IN_PROGRESS: 'In Arbeit', DONE: 'Fertig', ARCHIVED: 'Archiviert',
};

const statusStyles: Record<string, string> = {
  INQUIRY:     'bg-slate-100 text-slate-600',
  OFFER_SENT:  'bg-amber-50 text-amber-700 border border-amber-200',
  ORDER:       'bg-blue-50 text-blue-700 border border-blue-200',
  IN_PROGRESS: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  DONE:        'bg-green-50 text-green-600',
  ARCHIVED:    'bg-slate-100 text-slate-400',
};

export default function ProjectsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Partial<Project> | null>(null);

  const { data: projects = [], isLoading } = useQuery<Project[]>({
    queryKey: ['projects', search],
    queryFn: () => api.get('/projects', { params: { search: search || undefined } }).then((r) => r.data),
  });

  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers'],
    queryFn: () => api.get('/customers').then((r) => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (p: Partial<Project>) =>
      p.id ? api.put(`/projects/${p.id}`, p) : api.post('/projects', p),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }); setEditing(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  const startEdit = (p?: Project) => {
    if (p) {
      setEditing({ ...p, address: p.address || { street: '', zip: '', city: '' } });
    } else {
      setEditing({ title: '', customerId: '', status: 'INQUIRY', description: '', address: { street: '', zip: '', city: '' } });
    }
  };

  const addr = (p: Project) => {
    const a = p.address;
    if (!a) return null;
    const parts = [a.street, [a.zip, a.city].filter(Boolean).join(' ')].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const mapsUrl = (p: Project) => {
    const a = p.address;
    if (!a) return null;
    const q = [a.street, a.zip, a.city].filter(Boolean).join(', ');
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
  };

  const active = projects.filter((p) => p.status !== 'ARCHIVED' && p.status !== 'DONE').length;

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Projekte</h1>
          <p className="sp-page-subtitle">{projects.length} Projekte insgesamt &middot; {active} aktiv</p>
        </div>
        <button onClick={() => startEdit()} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Neues Projekt
        </button>
      </div>

      <div className="sp-search">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input placeholder="Projekte suchen..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {editing && (
        <div className="sp-form-card">
          <h3 className="sp-form-title">{editing.id ? 'Projekt bearbeiten' : 'Neues Projekt'}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Titel *</label>
              <input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="sp-input" placeholder="Projektname" autoFocus />
            </div>
            <div>
              <label className="sp-label">Kunde *</label>
              <select value={editing.customerId || ''} onChange={(e) => setEditing({ ...editing, customerId: e.target.value })}
                className="sp-input">
                <option value="">Kunde wählen...</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select value={editing.status || 'INQUIRY'} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                className="sp-input">
                {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="sp-label">Startdatum</label>
              <input type="date" value={(editing.startDate || '').slice(0, 10)}
                onChange={(e) => setEditing({ ...editing, startDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                className="sp-input" />
            </div>
            <div className="col-span-2">
              <label className="sp-label">Beschreibung</label>
              <textarea value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="sp-input min-h-[60px] resize-y" placeholder="Projektbeschreibung..." />
            </div>
          </div>

          {/* Baustellenadresse */}
          <div className="mt-5 pt-5 border-t border-surface-100">
            <h4 className="text-[13px] font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Baustellenadresse
            </h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="sp-label">Straße + Hausnummer</label>
                <input value={editing.address?.street || ''}
                  onChange={(e) => setEditing({ ...editing, address: { ...editing.address, street: e.target.value } })}
                  className="sp-input" placeholder="Musterstraße 123" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="sp-label">PLZ</label>
                  <input value={editing.address?.zip || ''}
                    onChange={(e) => setEditing({ ...editing, address: { ...editing.address, zip: e.target.value } })}
                    className="sp-input" placeholder="12345" />
                </div>
                <div className="col-span-2">
                  <label className="sp-label">Ort</label>
                  <input value={editing.address?.city || ''}
                    onChange={(e) => setEditing({ ...editing, address: { ...editing.address, city: e.target.value } })}
                    className="sp-input" placeholder="Berlin" />
                </div>
              </div>
            </div>
          </div>

          <div className="sp-form-actions">
            <button onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending} className="sp-btn-primary">
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
            <button onClick={() => setEditing(null)} className="sp-btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="sp-card overflow-hidden animate-fade-in">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Projekt</th>
              <th>Kunde</th>
              <th>Baustelle</th>
              <th>Status</th>
              <th className="w-28 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5}><div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                <svg className="sp-spinner" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                <span className="text-sm">Laden...</span>
              </div></td></tr>
            ) : projects.length === 0 ? (
              <tr><td colSpan={5}>
                <div className="sp-empty">
                  <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Keine Projekte gefunden</span>
                </div>
              </td></tr>
            ) : projects.map((p) => (
              <tr key={p.id} onClick={() => startEdit(p)} className="cursor-pointer hover:bg-brand-50/50 transition-colors">
                <td>
                  <div>
                    <span className="font-medium text-slate-800 block">{p.title}</span>
                    {p.description && <span className="text-[11px] text-slate-400 line-clamp-1">{p.description}</span>}
                  </div>
                </td>
                <td className="text-slate-500">{p.customer?.name || '-'}</td>
                <td>
                  {addr(p) ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{addr(p)}</span>
                      {mapsUrl(p) && (
                        <a href={mapsUrl(p)!} target="_blank" rel="noreferrer"
                          className="sp-btn-icon !w-7 !h-7 !text-brand-400 hover:!text-brand-600 hover:!bg-brand-50 flex-shrink-0"
                          title="Navigation starten">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-300">Keine Adresse</span>
                  )}
                </td>
                <td>
                  <span className={`sp-badge ${statusStyles[p.status] || 'bg-slate-100 text-slate-600'}`}>
                    {statusLabels[p.status] || p.status}
                  </span>
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <button onClick={() => startEdit(p)} className="sp-btn-icon" title="Bearbeiten">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => { if (confirm('Projekt wirklich löschen?')) deleteMutation.mutate(p.id); }}
                      className="sp-btn-icon hover:!text-error-500 hover:!bg-error-50" title="Löschen">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
